import { useEffect, useRef, useState } from "react";
import {
  Table,
  Title,
  Button,
  Container,
  Pagination,
  Loader,
  Center,
  Code,
  Group,
  TextInput,
  Text,
} from "@mantine/core";
import { Link, useLocation } from "wouter";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch } from "@tabler/icons-react";
import { useTenantStore } from "./tenantStore";

function TenantList() {
  const [, setLocation] = useLocation();

  const allTenants = useTenantStore((state) => state.allTenants);
  const fetchTenants = useTenantStore((state) => state.fetchTenants);

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [activePage, setActivePage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const appliedSearch = useRef("");

  const pageSize = 50;
  const searchTerm = debouncedSearch.trim();

  useEffect(() => {
    let cancelled = false;
    if (appliedSearch.current !== searchTerm) {
      appliedSearch.current = searchTerm;
      if (activePage !== 1) {
        setActivePage(1);
        return () => {
          cancelled = true;
        };
      }
    }

    setIsLoading(true);
    fetchTenants(activePage, pageSize, searchTerm || undefined).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [activePage, searchTerm, fetchTenants]);

  if (!allTenants) {
    return (
      <Center mt="xl">
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <Container size="md">
      <Group justify="space-between" align="flex-end" mb="md">
        <Title order={1}>Tenants</Title>
        <Button component={Link} to="/tenants/create">
          New Tenant
        </Button>
      </Group>

      <TextInput
        placeholder="Search by name or id"
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
        mb="md"
      />

      <Pagination
        hideWithOnePage={false}
        total={Math.max(1, Math.ceil(allTenants.totalCount / pageSize))}
        value={activePage}
        onChange={setActivePage}
      />
      {isLoading ? <Loader size="sm" /> : null}

      <Table highlightOnHover stickyHeader stickyHeaderOffset={60}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Id</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Plan</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {allTenants.data?.length === 0 && !isLoading && (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Text c="dimmed" ta="center" py="lg">
                  {searchTerm
                    ? "No tenants match that search."
                    : "No tenants yet."}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {allTenants.data?.map((p) => (
            <Table.Tr
              key={p.id}
              onClick={() => setLocation(`/tenants/${p.id}`)}
              style={{ cursor: "pointer" }}
            >
              <Table.Td>
                <Code fz="sm">{p.id}</Code>
              </Table.Td>
              <Table.Td>{p.name}</Table.Td>
              <Table.Td>{p.plan}</Table.Td>
              <Table.Td>{p.created}</Table.Td>
              <Table.Td>
                <Group gap="xs" justify="flex-end" wrap="nowrap">
                  <Button
                    size="compact-sm"
                    variant="default"
                    component={Link}
                    to={`/admin/users?tenantId=${p.id}`}
                    onClick={(evt) => evt.stopPropagation()}
                  >
                    Users
                  </Button>
                  <Button
                    size="compact-sm"
                    onClick={(evt) => {
                      evt.stopPropagation();
                      setLocation(`/tenants/${p.id}/users/create`);
                    }}
                  >
                    + User
                  </Button>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Container>
  );
}

export default TenantList;
